/**
 * PV Quiz - Klaviyo (Email Step submit only)
 *
 * Shared by the PV Quiz Viking and CROEV Quiz V1 engines. Called once the
 * shopper submits the Email Step with a valid name + email:
 *   1. klaviyo.identify()  - email, first name, answers as pv_quiz_* props
 *      (onsite klaviyo.js, so the browser is identified for later tracking)
 *   2. 'Primal Quiz Completed' event via the Client API - always, with the
 *      same profile data, so profile + event exist even without consent
 *   3. Client API subscribe to "PV: Quiz Funnel" - only if consent is ticked
 *
 * submit()
 * always resolves (never rejects) and never takes longer than TIMEOUT_MS, so
 * a slow/blocked Klaviyo can never stop the shopper from reaching results.
 */

(function () {
  'use strict';

  var LIST_ID = 'Tv9cd8'; // PV: Quiz Funnel
  // Public (site) key - normally read from the klaviyo.js tag the app embed
  // injects; this is the same key, used only if that tag isn't found.
  var FALLBACK_PUBLIC_KEY = 'WDbifq';
  var API_REVISION = '2024-10-15';
  var EVENT_NAME = 'Primal Quiz Completed';
  var TIMEOUT_MS = 2000;

  function getPublicKey() {
    var script = document.querySelector('script[src*="klaviyo.js?company_id="]');
    var match = script && /company_id=([A-Za-z0-9]+)/.exec(script.src);
    return match ? match[1] : FALLBACK_PUBLIC_KEY;
  }

  // Never let a Klaviyo call throw or reject into the quiz flow.
  function safely(fn) {
    try {
      return Promise.resolve(fn()).catch(function (err) {
        console.warn('PV Quiz Klaviyo:', err);
      });
    } catch (err) {
      console.warn('PV Quiz Klaviyo:', err);
      return Promise.resolve();
    }
  }

  function buildProfileProperties(data) {
    var props = {
      pv_quiz_template: data.quizTemplate,
      pv_quiz_completed_at: new Date().toISOString()
    };
    if (data.recommendedProduct) props.pv_quiz_result = data.recommendedProduct;
    Object.keys(data.answers || {}).forEach(function (key) {
      props['pv_quiz_' + key] = data.answers[key];
    });
    return props;
  }

  function identify(data, properties) {
    var payload = Object.assign({ email: data.email, first_name: data.firstName }, properties);
    if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
      return window.klaviyo.identify(payload);
    }
    // Legacy onsite queue (older embed).
    window._learnq = window._learnq || [];
    window._learnq.push([
      'identify',
      Object.assign({ $email: data.email, $first_name: data.firstName }, properties)
    ]);
  }

  // Sent straight to the Client API (not klaviyo.track): onsite tracking only
  // fires for an already-identified browser and can be held back by the
  // cookie/consent setup, which silently dropped the event whenever the
  // consent box was unticked. This call upserts the profile (email, name,
  // pv_quiz_* props) and logs the event in one request, consent or not.
  function track(data, properties) {
    var eventProps = Object.assign(
      { quiz_template: data.quizTemplate },
      data.answers,
      {
        recommended_product: data.recommendedProduct || null,
        page_url: window.location.href
      }
    );
    return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(getPublicKey()), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: API_REVISION },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties: eventProps,
            metric: { data: { type: 'metric', attributes: { name: EVENT_NAME } } },
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: data.email,
                  first_name: data.firstName,
                  properties: properties
                }
              }
            }
          }
        }
      })
    }).then(function (response) {
      if (!response.ok) throw new Error('Event failed (' + response.status + ')');
    });
  }

  function subscribe(data, properties) {
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(getPublicKey()), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: API_REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'PV Quiz - ' + data.quizTemplate,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: data.email,
                  first_name: data.firstName,
                  properties: properties
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: LIST_ID } }
          }
        }
      })
    }).then(function (response) {
      if (!response.ok) throw new Error('Subscribe failed (' + response.status + ')');
    });
  }

  /**
   * @param {{ email: string, firstName: string, consent: boolean,
   *           quizTemplate: string, answers: Object, recommendedProduct?: string }} data
   * @returns {Promise<void>} resolves when done or after TIMEOUT_MS, whichever is first
   */
  function submit(data) {
    var properties = buildProfileProperties(data);
    var calls = [
      safely(function () { return identify(data, properties); }),
      safely(function () { return track(data, properties); })
    ];
    // Unticked consent: profile + event only, no marketing subscription.
    if (data.consent) {
      calls.push(safely(function () { return subscribe(data, properties); }));
    }

    var timeout = new Promise(function (resolve) {
      setTimeout(resolve, TIMEOUT_MS);
    });
    return Promise.race([Promise.all(calls), timeout]).then(function () {});
  }

  window.PVQuizKlaviyo = { submit: submit };
})();
